import { useCallback } from "react";

import theme from "theme";
import { colors } from "theme/palette";

import Stack from "components/Stack";
import Switch from "components/Switch";
import Tag from "components/Tag";
import Text from "components/Text";
import ToggleButtonGroup, { ToggleButton } from "components/ToggleButtonGroup";
import { useFeatureDisabledDialog } from "components/useFeatureDisabledDialog";

import {
  ECompanyFeatureType,
  ECompanySettingsFeatureType,
} from "entities/Company/components/CompanySettings/constants";
import Feature from "entities/Company/components/CompanySettings/Feature";
import {
  COMPANY_SETTINGS_FEATURE_VISIBLE_TYPES_OPTIONS,
  COMPANY_SETTINGS_OPTIONS,
  ECompanySettingsFeatureVisibleType,
  FEATURE_VISIBILITY_TO_TAG_PROPS_MAPPING,
} from "entities/Company/components/CompanySettings/FeaturesList/constants";
import { ICompanySettings } from "entities/Company/sdk";

import { getCompanySettingsFeatureVisibleType } from "./utils";

export interface IFeaturesList {
  companySettings: ICompanySettings;
  editable?: boolean;
  onChange?: ({
    feature,
    value,
  }: {
    feature: ECompanySettingsFeatureType | ECompanyFeatureType;
    value: ECompanySettingsFeatureVisibleType;
  }) => void;
  showPayingCustomerSwitch?: boolean;
}

const FeaturesList = ({
  companySettings,
  editable = false,
  onChange,
  showPayingCustomerSwitch = true,
}: IFeaturesList) => {
  const { show: showFeatureDisabledDialog } = useFeatureDisabledDialog();
  const handleChange = useCallback(
    ({
      feature,
      value,
    }: {
      feature: ECompanySettingsFeatureType | ECompanyFeatureType;
      value: ECompanySettingsFeatureVisibleType;
    }) => {
      if (editable && onChange) {
        onChange({ feature, value });
      } else {
        showFeatureDisabledDialog({});
      }
    },
    [editable, onChange, showFeatureDisabledDialog]
  );

  const handleFeatureVisibility = useCallback(
    ({ feature }: { feature: ECompanySettingsFeatureType }) =>
      getCompanySettingsFeatureVisibleType({
        feature,
        companySettings,
      }),
    [companySettings]
  );

  return (
    <Stack>
      {COMPANY_SETTINGS_OPTIONS.map(({ value: featureValue, label }) => {
        const featureVisibilityValue = handleFeatureVisibility({
          feature: featureValue,
        });

        return (
          <Feature key={featureValue} label={label}>
            <ToggleButtonGroup
              exclusive
              color="primary"
              size="small"
              value={handleFeatureVisibility({
                feature: featureValue,
              })}
              onChange={(_, value: ECompanySettingsFeatureVisibleType) =>
                handleChange({ feature: featureValue, value: value })
              }
            >
              {editable ? (
                COMPANY_SETTINGS_FEATURE_VISIBLE_TYPES_OPTIONS.map(
                  ({ value, label }) => (
                    <ToggleButton
                      key={value}
                      value={value}
                      disabled={value === featureVisibilityValue}
                    >
                      {label}
                    </ToggleButton>
                  )
                )
              ) : (
                <Tag
                  {...FEATURE_VISIBILITY_TO_TAG_PROPS_MAPPING[
                    featureVisibilityValue
                  ]}
                />
              )}
            </ToggleButtonGroup>
          </Feature>
        );
      })}
      {showPayingCustomerSwitch && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          borderBottom={`1px solid ${colors.blue20}`}
          padding={theme.spacing(0.5, 0)}
        >
          <Text>Paying Customer</Text>
          <Switch
            onChange={(_, value) => {
              handleChange({
                feature: ECompanyFeatureType.PAYING_CUSTOMER,
                value: value
                  ? ECompanySettingsFeatureVisibleType.ENABLED
                  : ECompanySettingsFeatureVisibleType.DISABLED,
              });
            }}
            checked={companySettings.is_paying_customer}
            value={companySettings.is_paying_customer}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default FeaturesList;
